import type { ToggleGroupSingleProps } from "@radix-ui/react-toggle-group";
import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group";
import clsx from "clsx";
import {
  segmentedSwitchItem,
  segmentedSwitchRoot,
  segmentedSwitchThumb
} from "./SegmentedSwitch.css";

/** One side of a SegmentedSwitch. */
export interface SegmentedSwitchOption {
  /** The value this side represents. */
  value: string;
  /** Label rendered on this side. */
  label: string;
}

/** Props for the SegmentedSwitch component. */
export interface SegmentedSwitchProps extends Omit<
  ToggleGroupSingleProps,
  "type" | "value" | "defaultValue" | "onValueChange" | "onChange" | "children"
> {
  /** The two sides, left then right. */
  options: [SegmentedSwitchOption, SegmentedSwitchOption];
  /** The value of the side selected. */
  value: string;
  /** Called with a side's value when the user picks it. */
  onChange: (value: string) => void;
}

/**
 * A pick between exactly two options, its thumb sliding under the one
 * selected, as the theme switcher does. Each side is a radio to assistive
 * tech, so it hears which is picked.
 */
export function SegmentedSwitch({
  options,
  value,
  onChange,
  className,
  ...rest
}: SegmentedSwitchProps) {
  function handleValueChange(next: string) {
    // Radix fires "" for a click on the side already picked; one stays picked.
    if (next) onChange(next);
  }

  return (
    <ToggleGroupPrimitive.Root
      type="single"
      value={value}
      onValueChange={handleValueChange}
      className={clsx(segmentedSwitchRoot, className)}
      {...rest}
    >
      <span
        aria-hidden
        className={segmentedSwitchThumb}
        data-side={value === options[1].value ? "right" : "left"}
      />
      {options.map((option) => (
        <ToggleGroupPrimitive.Item
          key={option.value}
          value={option.value}
          className={segmentedSwitchItem}
        >
          {option.label}
        </ToggleGroupPrimitive.Item>
      ))}
    </ToggleGroupPrimitive.Root>
  );
}
